import React, {
  useState, useEffect,
} from 'react'
import {
  Typography, Box,
} from '@material-ui/core'
import { ExpandMore } from '@material-ui/icons'
import { cx } from 'linaria'
import { useRouter } from 'next/router'
import classes from './ImageCard.styles'
import StyledButton from '../StyledButton/StyledButton'
import Wysiwyg from '../Wysiwyg/Wysiwyg'
import MediaImage, { MediaImageProps } from '../Media/MediaImage'
import theme, {
  BORDERS,
  GENERAL, ROUNDED, general,
} from '../../styles/theme'
import Wrapper from '../Wrapper/Wrapper'

export interface Button {
  text: string;
  url: string;
  modal: boolean;
}

enum CTATypeEnum {
  button = 'button',
  card_click = 'card_click'
}

enum AvailabilityEnum {
  available = 'available',
  coming_later = 'coming_later'
}

export interface ImageCardItemProps {
  readonly name: string;
  readonly title: string;
  readonly description: string;
  readonly button: Button;
  readonly cta_type?: CTATypeEnum;
  readonly availability?: AvailabilityEnum;
  readonly media_image?: MediaImageProps;
}

export interface ImageCardProps {
  readonly heading?: string;
  readonly subheading?: string;
  readonly image_card_item: ImageCardItemProps[];
  readonly anchor_ref?: string;
}

export default function ImageCard({
  heading,
  subheading,
  image_card_item,
  anchor_ref,
}: ImageCardProps) {
  const router = useRouter()

  const [
    showLoadMoreBtn,
    setShowLoadMoreBtn,
  ] = useState(true)

  const [
    loadMoreStep,
    setLoadMoreStep,
  ] = useState(0)

  const [
    imageCards,
    setImageCards,
  ] = useState<ImageCardItemProps[]>([])

  const cardClick = (item: ImageCardItemProps) => {
    if (!item.button || (item.button && item.cta_type !== CTATypeEnum.card_click)) {
      return null
    }

    return router.push(item.button.url)
  }

  const numberOfCards = image_card_item?.length

  const onLoadMoreClick = () => {
    const nextloadMoreStep = loadMoreStep + (window.innerWidth < 960 ? 6 : 10)

    const visibleImageCards = [
      ...imageCards,
      ...image_card_item.slice(loadMoreStep, nextloadMoreStep),
    ]
    setImageCards(visibleImageCards)

    setLoadMoreStep(nextloadMoreStep)

    if (nextloadMoreStep > image_card_item.length) {
      setShowLoadMoreBtn(false)
    }
  }

  useEffect(() => {
    if (image_card_item?.length) {
      onLoadMoreClick()
    }
  }, [image_card_item])

  return (
    <Wrapper id={anchor_ref} verticalPaddings>
      {heading &&
        <Typography variant="h2" className={classes.title}>
          {heading}
        </Typography>}
      {subheading && <Wysiwyg variant="body1" body={subheading} className={classes.bodyText}/>}
      <Box
        display={{
          xs: 'flex',
          md: 'grid',
        }}
        flexDirection="column"
        gridTemplateColumns={`repeat(${numberOfCards}, 1fr)`}
        gridGap={theme.spacing(numberOfCards === 4 ? 3 : 4)}
        alignItems={{
          xs: 'center',
          md: 'stretch',
        }}
      >
        {imageCards.map((item, index) => {
          const isSecondaryBtnHidden = (!Object.keys(item?.button).length) || (Object.keys(item?.button).length && item.cta_type === CTATypeEnum.card_click)

          return (
            <Box
              className={cx(item.cta_type === CTATypeEnum.card_click ? classes.cardClick : undefined, classes.outerCard)}
              onClick={() => cardClick(item)}
              display="flex"
              flexDirection="column"
              key={index}
              maxWidth={{
                xs: GENERAL.addOnWidthMobile,
                lg: '100%',
              }}
              minWidth={{
                xs: '100%',
                lg: '270px',
              }}
            >
              <Box
                borderRadius={`${ROUNDED.medium}px ${ROUNDED.medium}px 0 0`}
                overflow="hidden"
                borderBottom={`${BORDERS.gray3}`}
              >
                {item.media_image?.src &&
                <MediaImage
                  src={item.media_image.src}
                  width={item.media_image.width || 295}
                  height={item.media_image.height || 168}
                  alt={item.media_image.alt}
                  mobile_src={item.media_image.mobile_src}
                  mobile_width={item.media_image.mobile_width}
                  mobile_height={item.media_image.mobile_height}
                  full_width
                  cover
                />}
                {item.availability &&
                <div className={cx(classes.availability, item.availability === AvailabilityEnum.available ? classes.bgPrimary : classes.bgSecondary)}>
                  <Typography variant="subtitle2">{item.availability === AvailabilityEnum.available ? 'Available' : 'Coming later'}</Typography>
                </div>}
              </Box>
              <Box flexGrow={1} pt={4} px={numberOfCards === 4 ? 3 : 5} pb={3} textAlign="center">
                {item.title &&
                  <Typography variant="h4" color="primary" style={{ marginBottom: theme.spacing(2) }}>
                    {item.title}
                  </Typography>}
                <Typography
                  className={cx(isSecondaryBtnHidden ? classes.noButton : undefined, general.font.weight.book)}
                  variant="body1"
                  color="textPrimary"
                  component="div"
                >
                  {/* eslint-disable-next-line react/no-danger */}
                  <div dangerouslySetInnerHTML={{ __html: item.description }}/>
                </Typography>
              </Box>
              {(Boolean(Object.keys(item?.button).length) && item.cta_type !== CTATypeEnum.card_click) &&
              <Box px={5} pb={numberOfCards === 4 ? 3 : 4} pt={0}>
                <StyledButton
                  isModal={item.button?.modal}
                  color="secondary"
                  variant="outlined"
                  url={item.button?.url}
                  isFullWidth
                >
                  {item.button?.text}
                </StyledButton>
              </Box>}
            </Box>
          )
        })}
      </Box>
      {showLoadMoreBtn &&
      <Box display="flex" justifyContent="center" alignItems="center" className={classes.loadMoreContainer}>
        <button className={classes.loadMoreBtn} type="button" onClick={() => onLoadMoreClick()}>
          <Typography variant="body1" color="primary" className={classes.loadMoreBtnText}>Load more <ExpandMore className={classes.iconPadding} color="primary"/></Typography>
        </button>
      </Box>}
    </Wrapper>
  )
}
